import { Popup, Dropdown, Button } from "semantic-ui-react";

import { selectColumn } from "components/BaseTableV3/selectColumn";
import ACL_RELATIONSHIPS from "acl-relationships";
import { createDateTimeFormatter } from "components/helpers";
import withRoleCheck from "components/hocs/withRoleCheck";
import TagList from "components/TagList";
import CustomDropdown from "components/CustomDropdown";
import Constants from "constants/Constants";
import { DeleteJobModal, ImportModal } from "./components/Modals";
import { JobProgress } from "./JobProgress";
import { DATA_JOB_TABLE_CONSTANTS } from "./DataJobTable";
import DataJobService from "services/DataJob";

const { DATA_JOB_STATUSES } = Constants;

const DeleteJobModalWithRole = withRoleCheck(DeleteJobModal, [
  ACL_RELATIONSHIPS.dataJob.delete,
]);

const ImportModalTriggerWithRole = withRoleCheck(ImportModal.Trigger, [
  ACL_RELATIONSHIPS.dataJob.read,
  ACL_RELATIONSHIPS.importData.edit,
]);

const getStatusKey = (status, info) => {
  let statusKey;

  if (info?.aborted) {
    statusKey = DATA_JOB_TABLE_CONSTANTS.STATUS_TEXT.ABORTED;
    return statusKey?.toUpperCase();
  }

  for (const JOB_STATUS in DATA_JOB_STATUSES) {
    if (DATA_JOB_STATUSES[JOB_STATUS] === status) {
      statusKey = JOB_STATUS.replace(/_/g, " ");
      break;
    }
  }

  return statusKey?.toUpperCase() || "";
};

async function getMonitorJobProgress({ jobId, status, callback }) {
  try {
    const dataJob = await DataJobService.getDataJob(jobId);

    if (dataJob.status !== status) {
      callback && callback();
    }

    return dataJob.status;
  } catch (error) {
    console.error("Error monitoring job progress:", error);
  }
}

export function getDataJobTableColumns({
  onDeleteJob,
  onOpenImportModal,
  onOpenCaseDataImportModal,
  invalidateData,
  className = DATA_JOB_TABLE_CONSTANTS.className,
}) {
  const formatCreatedAt = createDateTimeFormatter({
    format: DATA_JOB_TABLE_CONSTANTS.DATE_FORMAT.FORMAT,
  });

  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: DATA_JOB_TABLE_CONSTANTS.COLUMN_SIZES.ID,
      cell: ({ row }) => (
        <span style={{ fontWeight: "bold" }}>{row.getValue("ID")}</span>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags") || []}
          modelType={className}
          modelId={row.getValue("ID")}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Job Type",
      id: "Job Type",
      accessorKey: "type",
    },
    {
      header: "Description",
      id: "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <Popup
          content={row.getValue("Description")}
          trigger={<span>{row.getValue("Description")}</span>}
        />
      ),
    },
    {
      header: "Submitter",
      id: "Submitter",
      accessorKey: "submitter",
    },
    {
      header: "Created At",
      id: "Created At",
      accessorKey: "created_at",
      size: DATA_JOB_TABLE_CONSTANTS.COLUMN_SIZES.CREATED_AT,
      cell: ({ row }) => <p>{formatCreatedAt(row.getValue("Created At"))}</p>,
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "status",
      size: DATA_JOB_TABLE_CONSTANTS.COLUMN_SIZES.STATUS,
      cell: ({ row }) => (
        <p>{getStatusKey(row.getValue("Status"), row.original.info || {})}</p>
      ),
    },
    {
      header: "Progress",
      id: "Progress",
      accessorKey: "progress",
      size: DATA_JOB_TABLE_CONSTANTS.COLUMN_SIZES.PROGRESS,
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => (
        <JobProgress
          jobId={row.getValue("ID")}
          status={row.getValue("Status")}
          info={row.original.info || {}}
          invalidateData={invalidateData}
          getMonitorJobProgress={getMonitorJobProgress}
        />
      ),
    },
    {
      header: "Result",
      id: "Result",
      accessorKey: "result",
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => {
        const type = row.original.type;
        const id = row.original.id;
        if (
          type?.startsWith(DATA_JOB_TABLE_CONSTANTS.JOB_TYPE_PREFIXES.EXPORT)
        ) {
          return DATA_JOB_TABLE_CONSTANTS.DISPLAY_TEXT.EXPORT_PLACEHOLDER;
        }
        return (
          <ImportModalTriggerWithRole
            content={DATA_JOB_TABLE_CONSTANTS.DISPLAY_TEXT.CHECK_RESULT}
            size="mini"
            onClick={() =>
              type === DATA_JOB_TABLE_CONSTANTS.JOB_TYPES.CASE_DATA_IMPORT
                ? onOpenCaseDataImportModal(id)
                : onOpenImportModal(id)
            }
          />
        );
      },
    },
    {
      header: "Download URL",
      id: "Download URL",
      accessorKey: "download_url",
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const downloadUrl = row.getValue("Download URL");
        if (downloadUrl) {
          return (
            <Popup
              content={
                row.original.file_name
                  ? row.original.file_name
                  : DATA_JOB_TABLE_CONSTANTS.DISPLAY_TEXT.NO_FILE_NAME
              }
              trigger={
                <Button primary size="mini" href={downloadUrl}>
                  {DATA_JOB_TABLE_CONSTANTS.DISPLAY_TEXT.DOWNLOAD_HERE}
                </Button>
              }
            />
          );
        } else {
          return <p>{DATA_JOB_TABLE_CONSTANTS.DISPLAY_TEXT.NO_LINK}</p>;
        }
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: DATA_JOB_TABLE_CONSTANTS.COLUMN_SIZES.ACTIONS,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => {
        const id = row.getValue("ID");
        const type = row.original.type;
        const status = row.getValue("Status");
        return (
          <CustomDropdown icon="ellipsis horizontal">
            <Dropdown.Menu direction="left">
              {(type?.startsWith(
                DATA_JOB_TABLE_CONSTANTS.JOB_TYPE_PREFIXES.IMPORT
              ) ||
                type?.startsWith(
                  DATA_JOB_TABLE_CONSTANTS.JOB_TYPE_PREFIXES.VALIDATION
                )) &&
                status !== Constants.DATA_JOB_STATUSES.EXPIRED && (
                  <ImportModalTriggerWithRole
                    status={status}
                    dropdown
                    onClick={() => onOpenImportModal(id)}
                  />
                )}
              <DeleteJobModalWithRole
                onConfirmDelete={() => onDeleteJob(id)}
                menuTrigger
              />
            </Dropdown.Menu>
          </CustomDropdown>
        );
      },
    },
  ];
}

export const getDataJobTableFilters = ({
  statuses = [],
  users = [],
  types = [],
  tags = [],
}) => {
  return [
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.STATUS,
      title: "Status",
      type: "select",
      data: statuses,
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.MODEL,
      title: "Type",
      type: "select",
      data: [
        {
          id: DATA_JOB_TABLE_CONSTANTS.FILTER_MODEL_TYPES.AUDIT_LOG,
          name: "Audit Log",
        },
        {
          id: DATA_JOB_TABLE_CONSTANTS.FILTER_MODEL_TYPES.CONTACT,
          name: "Contact",
        },
        {
          id: DATA_JOB_TABLE_CONSTANTS.FILTER_MODEL_TYPES.CAMPAIGN_TARGET,
          name: "Campaign Targets",
        },
      ],
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.ACTION,
      title: "Action",
      type: "select",
      data: [
        {
          id: DATA_JOB_TABLE_CONSTANTS.FILTER_ACTION_TYPES.IMPORT,
          name: "Import",
        },
        {
          id: DATA_JOB_TABLE_CONSTANTS.FILTER_ACTION_TYPES.EXPORT,
          name: "Export",
        },
      ],
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.CREATOR_USER_ID,
      title: "Submitter",
      type: "select",
      data: users,
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.JOB_TYPE,
      title: "Job Type",
      type: "select",
      data: types,
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.CREATED_DATE,
      title: "Created Date",
      type: "dateRange",
      _type: "advanced",
    },
    {
      key: DATA_JOB_TABLE_CONSTANTS.FILTER_KEYS.TAG_ID,
      title: "Tag",
      type: "tag",
      data: tags,
    },
  ];
};
